import { CalendarClock, EllipsisIcon, EyeOff, Info, Pencil, Play } from "lucide-react";
import Link from "next/link";
import type { ComponentProps, ReactNode } from "react";
import type { Workflow, WorkflowSampleResponse } from "@mlx/contracts";
import { Button } from "@/components/ui/button";
import { NativeSelect } from "@/components/ui/native-select";
import { Spinner } from "@/components/ui/spinner";
import { formatElapsedAgo } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { RunView } from "./workflow-trace-model";

/*
 * The workflow pane's chrome as markup: its floating toolbar (status line,
 * About, the actions menu's button), the workflow's controls (Edit, Schedule
 * and Test sample) and the shown run's controls, which float at the canvas's
 * top right, the Twin Judge notice and the body that keeps the canvas mounted
 * behind the other tabs. WorkflowPanel owns the workflow's
 * queries, sampling, draft and menus and renders through these, so a fixture
 * workflow and run compose the same pane.
 */

/** What a popover or menu trigger adds to a button: its handlers and trigger attributes. */
type TriggerProps = Omit<ComponentProps<typeof Button>, "children" | "size" | "variant">;

/** The ground of what floats over the canvas: its tools and the workflow's controls. */
export const canvasFloatingClass = "rounded-lg border border-border/70 bg-background shadow-[0_4px_16px_-6px_rgb(15_23_42/0.18)]";

export const JUDGE_SHARING = "Testing a sample sends each judge step’s state and questions, which can include source records, to TypeSafe when your organisation has enabled it. Sharing follows your organisation’s policy and needs no separate workflow approval.";

/**
 * One status line for the workflow, shown as the floating toolbar's subtitle:
 * what state it is in and whether its last test worked.
 */
export function workflowStatusText({ workflow, sample, running, editing, dirty, now, activation, operationalError }: {
  workflow: Workflow; sample?: WorkflowSampleResponse; running: boolean; editing: boolean; dirty: boolean;
  /** The clock the last sample's age is read against; the current time when omitted. */
  now?: number;
  activation?: { status: "active" | "paused" | "superseded"; workflowVersion: number } | null;
  operationalError?: boolean;
}) {
  const version = `v${workflow.version}`;
  if (editing) return `${version} · ${dirty ? "Unsaved changes" : "Editing"}`;
  if (workflow.archivedAt) return `${version} · Archived`;
  if (workflow.definition.schemaVersion === 2) {
    if (operationalError) return `${version} · Activation unavailable`;
    if (activation === undefined) return `${version} · Loading activation`;
    if (activation === null) return `${version} · Not activated`;
    const status = activation.status[0]!.toUpperCase() + activation.status.slice(1);
    return `${version} · ${status}${activation.workflowVersion !== workflow.version ? ` v${activation.workflowVersion} · New version needs review` : ""}`;
  }
  if (running) return `${version} · Sample running`;
  const preview = workflow.preview;
  if (!preview) return `${version} · Not tested`;
  if (preview.version !== workflow.version) return `${version} · Not tested since v${preview.version}`;
  const outcome = sample?.runId === workflow.previewRun?.id && sample?.status === "cancelled" ? "cancelled" : preview.status;
  const when = workflow.previewRun ? ` ${formatElapsedAgo(workflow.previewRun.createdAt, now)}` : "";
  return `${version} · Sample ${outcome === "succeeded" ? "passed" : outcome === "cancelled" ? "cancelled" : "failed"}${when}`;
}

/** Opens the workflow's About popover; the popover's trigger props pass through. */
export function WorkflowAboutTrigger(props: TriggerProps) {
  return <Button type="button" size="icon-sm" variant="ghost" className="size-7 text-muted-foreground"
    aria-label="About this workflow" title="About this workflow" {...props}><Info className="size-3.5" /></Button>;
}

/** A single-line notice at the top of the pane; the full text lives in the About popover. */
export function WorkflowJudgeNotice({ workflow }: { workflow?: Workflow }) {
  return workflow?.definition.nodes.some(node => node.kind === "judge") && !workflow.archivedAt ? <p role="note"
    className="shrink-0 truncate border-b border-border/60 bg-muted/30 px-3 py-1.5 text-[11px] text-muted-foreground" title={JUDGE_SHARING}>
    Twin Judge steps share their state with TypeSafe when you test a sample. Source records remain unchanged.
  </p> : null;
}

/** A draft's schedule as the toolbar shows it: when it runs, or that it stopped. */
export type WorkflowScheduleSummary = { label: string; stopped: boolean };

/**
 * The canvas's controls outside editing: Edit, Schedule (a draft's), then Test
 * sample (Operations for an operational workflow), or Restore.
 */
export function WorkflowViewControls({ workflow, busy, starting, schedule, onEdit, onSchedule, onTest,
  onRestore, onOperations }: {
  workflow: Workflow; busy: boolean; starting: boolean;
  /** The draft's schedule; null when it has none. Omit with onSchedule to hide the control. */
  schedule?: WorkflowScheduleSummary | null;
  onEdit?: () => void; onSchedule?: () => void; onTest?: () => void; onRestore?: () => void; onOperations?: () => void;
}) {
  if (workflow.archivedAt) return <Button type="button" size="sm" variant="outline" disabled={busy} onClick={onRestore}>Restore</Button>;
  return <>
    <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={onEdit}><Pencil className="size-3.5" />Edit</Button>
    {onSchedule && workflow.definition.schemaVersion === 1 ? <Button type="button" size="sm" variant="ghost"
      onClick={onSchedule} className={schedule?.stopped ? "text-destructive" : undefined}
      title={schedule ? schedule.stopped ? "The schedule stopped. Open it to see why."
        : `Runs ${schedule.label} and posts the result to a channel` : "Run on a schedule and post each result to a channel"}>
      <CalendarClock className="size-3.5" />{schedule ? schedule.stopped ? "Schedule stopped" : schedule.label : "Schedule"}
    </Button> : null}
    {workflow.definition.schemaVersion === 2
      ? <Button type="button" size="sm" variant="outline" onClick={onOperations}>Operations</Button>
      : <Button type="button" size="sm" disabled={busy} onClick={onTest} title="Runs a read-only sample. No channel records are changed.">
        {starting ? <Spinner className="size-3.5" aria-hidden /> : <Play className="size-3.5" />}{starting ? "Starting sample…" : "Test sample"}</Button>}
  </>;
}

/** Opens the workflow's actions (rename, archive or restore); the menu's trigger props pass through. */
export function WorkflowActionsTrigger({ workflowTitle, className, ...props }: { workflowTitle: string } & TriggerProps) {
  return <Button type="button" size="icon-sm" variant="ghost" title="Workflow actions" aria-label={`Actions for ${workflowTitle}`}
    className={cn("shrink-0 text-muted-foreground hover:text-foreground", className)} {...props}>
    <EllipsisIcon />
  </Button>;
}

/**
 * The run's own controls in the canvas's toolbar while a run is shown on it:
 * an operational item's attempt and record, where a scheduled run posted, and
 * Hide run. How the run went is told by its toast (useWorkflowRunToast).
 */
export function WorkflowRunControlsView({ run, post, onAttempt, onExit }: {
  run: RunView;
  /** Where a scheduled run posted its result. */
  post?: { href: string; channel: string };
  onAttempt?: (attempt: number) => void; onExit?: () => void;
}) {
  return <>
    {run.latestAttempt && run.latestAttempt > 1 && onAttempt ? <NativeSelect aria-label="Run attempt" className="h-7 w-auto text-xs"
      value={run.attempt} onChange={e => onAttempt(Number(e.target.value))}>
      {Array.from({ length: run.latestAttempt }, (_, i) => <option key={i + 1} value={i + 1}>Attempt {i + 1}</option>)}
    </NativeSelect> : null}
    {run.recordHref ? <Link href={run.recordHref} className={runLink}>Open record</Link> : null}
    {post && !run.live ? <a href={post.href} title={`Open the post in ${post.channel}`} className={runLink}>Open post</a> : null}
    <Button type="button" size="icon-sm" variant="ghost" className="text-muted-foreground hover:text-foreground" onClick={onExit}
      aria-label="Hide run from the canvas" title="Hide run (Esc)"><EyeOff className="size-3.5" /></Button>
  </>;
}
/** A run's links in the canvas's toolbar, sized as its buttons. */
const runLink = "inline-flex h-7 items-center whitespace-nowrap rounded-md px-2.5 text-[0.8rem] font-medium hover:bg-accent";

/** The pane's body: the canvas stays mounted behind the other tabs, so a run keeps playing on it. */
export function WorkflowPaneBodyView({ tab, canvas, children }: {
  tab: "canvas" | "runs" | "results" | "code";
  /** The canvas, mounted on every tab. */
  canvas: ReactNode;
  /** The open tab's body, when it is not the canvas. */
  children?: ReactNode;
}) {
  return <div className="min-h-0 flex-1">
    <div className={tab === "canvas" ? "h-full" : "hidden"}>
      {canvas}
    </div>
    {tab === "canvas" ? null : children}
  </div>;
}
